import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';

export const ProductModal = ({ isOpen, onClose, productTypes = [], onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    product_type: '',
    manufacturer: '',
    family: '',
    description: '',
    feature_values: {}
  });
  const [formErrors, setFormErrors] = useState({});
  const [localModalError, setLocalModalError] = useState(null);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
  const [selectedTypeFeatures, setSelectedTypeFeatures] = useState([]);

  const loadFeaturesForType = (typeName) => {
    const type = productTypes.find(t => t.name === typeName);
    if (type && type.features) {
      setSelectedTypeFeatures(type.features);
      // Initialize feature_values with empty strings
      const initialFeatureValues = {};
      type.features.forEach(feature => {
        initialFeatureValues[feature.name] = '';
      });
      setFormData(prev => ({
        ...prev,
        feature_values: initialFeatureValues
      }));
    } else {
      setSelectedTypeFeatures([]);
    }
  };

  // Initialize form when modal opens (Create mode only)
  useEffect(() => {
    if (isOpen) {
      setLocalModalError(null);
      setFormErrors({});
      // Reset form to default values for Create mode
      setFormData({
        name: '',
        product_type: '',
        manufacturer: '',
        family: '',
        description: '',
        feature_values: {}
      });
      setSelectedTypeFeatures([]);
    }
  }, [isOpen]);

  // Load features when product type changes
  useEffect(() => {
    if (formData.product_type) {
      loadFeaturesForType(formData.product_type);
    } else {
      setSelectedTypeFeatures([]);
      setFormData(prev => ({ ...prev, feature_values: {} }));
    }
  }, [formData.product_type]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.product_type.trim()) {
      errors.product_type = 'Product type is required';
    }

    // Validate feature values
    selectedTypeFeatures.forEach(feature => {
      const value = formData.feature_values[feature.name];
      if (feature.value_type === 'number' && value && value.toString().trim() !== '') {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          errors[`feature_${feature.name}`] = `${feature.name} must be a valid number`;
        }
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLocalIsSubmitting(true);
    setLocalModalError(null);

    try {
      // Prepare feature_values object (include all features, even if empty)
      const featureValues = {};
      selectedTypeFeatures.forEach(feature => {
        const value = formData.feature_values[feature.name];
        if (value !== undefined && value !== null && value.toString().trim() !== '') {
          // Backend expects feature_values as strings, even for numeric-like values
          featureValues[feature.name] = value.toString().trim();
        } else {
          // Include empty values as empty string
          featureValues[feature.name] = '';
        }
      });

      const productData = {
        name: formData.name.trim(),
        product_type: formData.product_type.trim(),
        manufacturer: formData.manufacturer.trim() || null,
        family: formData.family.trim() || null,
        description: formData.description.trim() || null,
        feature_values: featureValues
      };

      await onSave(productData);
      onClose();
    } catch (error) {
      setLocalModalError(error.message || 'Error saving product');
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFeatureValueChange = (featureName, value) => {
    setFormData(prev => ({
      ...prev,
      feature_values: {
        ...prev.feature_values,
        [featureName]: value
      }
    }));
    // Clear feature error when user starts typing
    const errorKey = `feature_${featureName}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white">
            Create Product
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            disabled={localIsSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* General error message */}
            {localModalError && (
              <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                <p className="text-red-300 text-sm">{localModalError}</p>
              </div>
            )}

            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                Basic Information
              </h3>

              {/* Name and Product Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.name ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="Product name"
                    disabled={localIsSubmitting}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Product Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="product_type"
                    value={formData.product_type}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.product_type ? 'border-red-500' : 'border-gray-600'
                    }`}
                    disabled={localIsSubmitting}
                  >
                    <option value="">Select a product type</option>
                    {productTypes.map(type => (
                      <option key={type.id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.product_type && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.product_type}</p>
                  )}
                </div>
              </div>

              {/* Manufacturer and Family */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Manufacturer"
                    disabled={localIsSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Family
                  </label>
                  <input
                    type="text"
                    name="family"
                    value={formData.family}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Family"
                    disabled={localIsSubmitting}
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Product description"
                  disabled={localIsSubmitting}
                />
              </div>
            </div>

            {/* Features Section */}
            {selectedTypeFeatures.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                  Product Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedTypeFeatures.map(feature => {
                    const errorKey = `feature_${feature.name}`;
                    const hasError = formErrors[errorKey];
                    return (
                      <div key={feature.id}>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {feature.name}
                          {feature.symbol && (
                            <span className="text-gray-500 ml-1">({feature.symbol})</span>
                          )}
                          {feature.unit && (
                            <span className="text-gray-500 ml-1">[{feature.unit}]</span>
                          )}
                        </label>
                        {feature.value_type === 'number' ? (
                          <input
                            type="number"
                            step="any"
                            value={formData.feature_values[feature.name] || ''}
                            onChange={(e) => handleFeatureValueChange(feature.name, e.target.value)}
                            className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              hasError ? 'border-red-500' : 'border-gray-600'
                            }`}
                            placeholder={`Enter ${feature.name}`}
                            disabled={localIsSubmitting}
                          />
                        ) : (
                          <input
                            type="text"
                            value={formData.feature_values[feature.name] || ''}
                            onChange={(e) => handleFeatureValueChange(feature.name, e.target.value)}
                            className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              hasError ? 'border-red-500' : 'border-gray-600'
                            }`}
                            placeholder={`Enter ${feature.name}`}
                            disabled={localIsSubmitting}
                          />
                        )}
                        {hasError && (
                          <p className="mt-1 text-sm text-red-400">{formErrors[errorKey]}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer with buttons */}
          <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={localIsSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              disabled={localIsSubmitting}
            >
              {localIsSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
